import type { Document, Model } from 'mongoose';

import {
  type TLocalizedText,
  toLocalizedText,
} from '../../helpers/localized-text.helpers';
import type { TFile } from './common.type';

export type MenuEntry = {
  title: TLocalizedText;
  type: 'webLink' | 'payload';
  payload: string;
};

export type MenuTemplate = Array<MenuEntry>;

export type HeaderScheme = {
  headerColor: string;
  headerTitleColor: string;
  headerSubtitleColor: string;
  windowColor: string;
  messageBarColor?: string;
  messageBarTextColor?: string;
};

export type MessageScheme = {
  messageBubbleColor: string;
  messageBubbleTextColor: string;
  personalMessageBubbleColor: string;
  personalMessageBubbleTextColor: string;
  buttonEdgeColor: string;
  buttonTextColor: string;
  buttonHoverColor: string;
  buttonBackgroundColor: string;
  carouselBackgroundColor: string;
  carouselButtonColor: string;
  carouselTextColor: string;
  carouselButtonTextColor: string;
  submitButtonColor: string;
  notificationAIGeneratedAnswerTextColor: string;
  streamingIndicatorColor: string;
  urlColor: string;
  urlClickColor: string;
  urlHoverColor: string;
  urlTextDecoration: 'underline' | 'none';
};

export type GreetingScheme = {
  titleColor: string;
  subtitleColor: string;
  buttonTextColor: string;
  buttonColor: string;
  backgroundColor: string;
};

export type Scheme = {
  header: HeaderScheme;
  message: MessageScheme;
  greeting: GreetingScheme;
};

export enum AnimationType {
  PULSE = 'pulse',
  BOUNCE = 'bounce',
  SCALE = 'scale',
  NONE = 'none',
}

export enum IconType {
  STATIC = 'static',
  ANIMATION = 'animation',
}

export type IconsWithFiles = {
  chatIcon: {
    open: TFile | string;
    closed: {
      type: IconType;
      path: TFile | string;
      animation?: AnimationType;
    };
  };
  logo: {
    type: 'circle' | 'square';
    path: TFile | string;
  };
  speechIcon: TFile | string;
};

export type GreetingOptionsWithFiles = {
  messageIcon: TFile | string;
  buttonText: TLocalizedText;
  allowLaterButton: boolean;
};

export type GreetingOptions = {
  messageIcon: string;
  buttonText: TLocalizedText;
  allowLaterButton: boolean;
};

export type Icons = {
  chatIcon: {
    open: string;
    closed: {
      type: IconType;
      path: string;
      animation?: AnimationType;
    };
  };
  logo: {
    type: 'circle' | 'square';
    path: string;
  };
  speechIcon: string;
};

export type ChatWindowState = 'greeting' | 'closed' | 'open' | 'fullscreen';

export type TWebclientAvailability = {
  type: 'always' | 'workprogram';
  timezone?: string;
  workProgram?: Record<string, { start: string; end: string }>;
};

export type TWebclientFonts = {
  chatWindow?: string;
};

export type BlockerConfig = {
  text: TLocalizedText;
  buttonText: TLocalizedText;
};

// Languages the Voice tab UI (labels, statuses, errors) can be shown in.
// The order here is the order of the language dropdown in the admin portal.
export const VOICE_UI_LANGUAGES = ['nl', 'en'] as const;

export type VoiceUILanguage = (typeof VOICE_UI_LANGUAGES)[number];

export const DEFAULT_VOICE_UI_LANGUAGE: VoiceUILanguage = 'nl';

export enum VoiceMode {
  Chat = 'chat',
  ChatAndVoice = 'chatAndVoice',
  VoiceOnly = 'voiceOnly',
}

export type VoiceConfig = {
  /**
   * Not read on a Voice A.I. Studio - the derivation short-circuits on the
   * studio type before it looks at this flag.
   */
  enabled: boolean;
  /**
   * Derived and response-only: the backend controller sets it on the config it
   * returns. Nothing persists it and no form writes it.
   */
  mode?: VoiceMode;
  elevenLabsAgentId: string;
  // Picks the Voice tab UI text and the language of the wrap-up instruction
  // sent to the agent. The language the agent itself speaks is configured on
  // the agent in ElevenLabs, so the two can differ.
  language?: VoiceUILanguage;
  // * Opt-in: seconds from the start of the call at which the ElevenLabs
  // * contextual update fires so the agent wraps up the call. Empty, null, or 0
  // * disables the signal. The suggested 255 sits about 45 s before the
  // * observed but unconfirmed 5-minute ElevenLabs maximum call length.
  wrapUpAtSeconds?: number | null;
};

export const resolveVoiceMode = (voice?: VoiceConfig): VoiceMode => {
  if (voice?.mode) {
    return voice.mode;
  }

  if (voice?.enabled) {
    return VoiceMode.ChatAndVoice;
  }

  return VoiceMode.Chat;
};

export type ConfigOptions = {
  welcomeMessage: TLocalizedText;
  waitingForTheBotResponseMessage: TLocalizedText;
  enableWaitingForBotResponseBlocker: boolean;
  notificationAIGeneratedAnswer: TLocalizedText;
  showWelcomeAsFirstMessage?: boolean;
  messagePlaceholder: TLocalizedText;
  popupTimeout: number;
  greetingTimeout?: number;
  allowTextInput?: boolean; // DEPRECATED: since we introduced the new buttonInteractionOnly field for specific blocks, but because of the old configs, it has to be kept until they are rewritten.
  useAttachment?: boolean;
  showCloseButton?: boolean;
  isSoundEnabled?: boolean;
  requireMicrosoftLogin?: boolean;
  language?: string;
  menuTemplate: MenuTemplate;
  defaultState?: ChatWindowState;
  headerType?: 'slim' | 'normal' | 'noimage';
  title: TLocalizedText;
  subtitle: TLocalizedText;
  refParam?: string;
  revisionId?: string;
  icons: Icons;
  greeting: GreetingOptions;
  availability?: TWebclientAvailability;
  blocker?: BlockerConfig;
  fonts: TWebclientFonts;
  voice?: VoiceConfig;
  useStreamingResponse?: boolean;
};

export type ConfigOptionsWithFiles = Omit<
  ConfigOptions,
  'icons' | 'greeting'
> & {
  icons: IconsWithFiles;
  greeting: GreetingOptionsWithFiles;
};

export type Config = {
  options: ConfigOptions;
  scheme: Scheme;
};

export type ConfigWithFiles = {
  options: ConfigOptionsWithFiles;
  scheme: Scheme;
};

export interface IWebclientConfigItem {
  identifier: string;
  name: string;
  isEnabled?: boolean;
  config: Config;
  isDefault?: boolean;
  deletedAt?: Date;
}

export interface IWebclientConfig {
  uuid: string;
  environment: string;
  companyId: string;
  botId: string;
  whitelistedDomains: string[];
  configs: IWebclientConfigItem[];
  isEnabledWebclientAccess: boolean;
}

export interface IWebclientConfigDocument extends Document, IWebclientConfig {}

export type IWebclientConfigModel = Model<IWebclientConfigDocument>;

export type TClientOptions = {
  ssoToken?: string;
};

// Arimo is a self-hosted, metric-compatible open substitute for Arial - it renders on
// Android (where Arial is not installed) so the default is consistent across platforms.
export const DEFAULT_CHAT_WINDOW_FONT_FAMILY = 'Arial, Arimo, sans-serif';

// `languages` and `defaultLanguage` come from Settings -> Bot Settings -> General.
// Every translatable text is expanded into a map keyed by those languages so the
// form renders one input per language.
export const getDefaultValuesForWebclient = (
  data: Config,
  languages: string[] = [],
  defaultLanguage = '',
) => {
  const localize = (text: TLocalizedText | undefined) =>
    toLocalizedText(text, languages, defaultLanguage);

  return {
    ...data,
    options: {
      ...data.options,
      showWelcomeAsFirstMessage: data.options.showWelcomeAsFirstMessage ?? true,
      title: localize(data.options.title),
      subtitle: localize(data.options.subtitle),
      welcomeMessage: localize(data.options.welcomeMessage),
      messagePlaceholder: localize(data.options.messagePlaceholder),
      waitingForTheBotResponseMessage: localize(
        data.options.waitingForTheBotResponseMessage ||
          'Please wait for the bot to finish responding.',
      ),
      enableWaitingForBotResponseBlocker:
        data.options.enableWaitingForBotResponseBlocker ?? false,
      notificationAIGeneratedAnswer: localize(
        data.options.notificationAIGeneratedAnswer || 'AI antwoord',
      ),
      blocker: {
        text: localize(data.options.blocker?.text),
        buttonText: localize(data.options.blocker?.buttonText),
      },
      menuTemplate: (data.options.menuTemplate || []).map((menuEntry) => ({
        ...menuEntry,
        title: localize(menuEntry.title),
      })),
      isSoundEnabled: data.options.isSoundEnabled ?? true,
      popupTimeout: data.options.popupTimeout || 0,
      availability: {
        ...data.options.availability,
        type: data.options.availability?.type || 'always',
      },
      greeting: {
        ...data.options.greeting,
        buttonText: localize(data.options.greeting?.buttonText),
        allowLaterButton: data.options.greeting?.allowLaterButton ?? true,
      },
      // Always filled in so the language dropdown has a valid selection even for
      // configs saved before the Voice tab existed.
      voice: {
        enabled: data.options.voice?.enabled ?? false,
        elevenLabsAgentId: data.options.voice?.elevenLabsAgentId || '',
        language: data.options.voice?.language || DEFAULT_VOICE_UI_LANGUAGE,
        wrapUpAtSeconds: data.options.voice?.wrapUpAtSeconds,
      },
    },
    scheme: {
      ...data.scheme,
      message: {
        ...data.scheme.message,
        buttonBackgroundColor:
          data.scheme.message?.buttonBackgroundColor || '#FFFFFF',
        notificationAIGeneratedAnswerTextColor:
          data.scheme.message?.notificationAIGeneratedAnswerTextColor ||
          '#bdbdbd',
        streamingIndicatorColor:
          data.scheme.message?.streamingIndicatorColor || '#99a1af',
        urlColor: data.scheme.message?.urlColor || '#0000EE',
        urlHoverColor: data.scheme.message?.urlHoverColor || '#0000EE',
        urlClickColor: data.scheme.message?.urlClickColor || '#0000EE',
        urlTextDecoration:
          data.scheme.message?.urlTextDecoration === 'underline',
      },
      header: {
        ...data.scheme.header,
        messageBarTextColor:
          data.scheme.header?.messageBarTextColor || '#4D4D4D',
      },
    },
  };
};
